import { useRef, useEffect, useState } from "react";
import "./CaseStudies.css";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import ScrollTrigger from "gsap/ScrollTrigger";
import { useTransitionNavigate } from "../../hooks/useTransitionNavigate";
import { CATEGORIES } from "../../config";

import { projects, media } from "./work";
import { fetchWorkMedia } from "../../utils/workMedia";
import LazyVideo from "../LazyVideo/LazyVideo";

gsap.registerPlugin(useGSAP, ScrollTrigger);

// One section per service category, in the services' order: case studies
// share one masonry grid: the featured card spans two columns, tiles (4:3)
// and loose media (natural ratio) take one. Data lives in work.js. Empty
// categories (Websites) get no section.
// Static media from work.js first, then anything added from /admin
const toGroups = (allMedia) =>
  Object.values(CATEGORIES)
    .map((category) => ({
      category,
      items: projects.filter((p) => p.category === category),
      shots: allMedia.filter((m) => m.category === category),
    }))
    .filter((g) => g.items.length || g.shots.length);

// Masonry on a plain CSS grid: rows are tiny (grid-auto-rows), and each cell
// spans as many as its content is tall. Re-measured whenever content resizes
// (media loading, viewport width).
function useMasonry(ref, count) {
  useEffect(() => {
    const grid = ref.current;
    const unit = () => parseFloat(getComputedStyle(grid).gridAutoRows);
    const fit = (cell) => {
      cell.style.gridRowEnd = `span ${Math.ceil(cell.firstElementChild.offsetHeight / unit())}`;
    };
    // Apply spans on the next frame, not inside the callback: changing the
    // page height there can toggle the scrollbar, resize the cells again in
    // the same frame and trigger the "ResizeObserver loop" error.
    let frame;
    const pending = new Set();
    const ro = new ResizeObserver((entries) => {
      entries.forEach((e) => pending.add(e.target.parentElement));
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        pending.forEach(fit);
        pending.clear();
      });
    });
    [...grid.children].forEach((cell) => ro.observe(cell.firstElementChild));
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [ref, count]); // re-attach when items arrive (remote media)
}

function Shot({ item }) {
  return item.video ? (
    <LazyVideo className="casestudies-shot" src={item.src} aria-label={item.alt || undefined} />
  ) : (
    <img className="casestudies-shot" src={item.src} alt={item.alt} loading="lazy" />
  );
}

function Group({ category, items, shots }) {
  const gridRef = useRef(null);
  useMasonry(gridRef, items.length + shots.length);
  return (
    <section className="casestudies-group">
      <p className="label casestudies-group-head" data-category={category}>{category}</p>
      <div className="casestudies-grid" ref={gridRef}>
        {items.map((p) => (
          <div key={p.path} className={`casestudies-cell${p.featured ? " casestudies-cell--feature" : ""}`}>
            <div className="casestudies-cell-inner">
              <ProjectCard project={p} />
            </div>
          </div>
        ))}
        {shots.map((m) => (
          <div key={m.id || m.src} className={`casestudies-cell${m.large ? " casestudies-cell--feature" : ""}`}>
            <div className="casestudies-cell-inner">
              <Shot item={m} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Media({ project }) {
  return project.video ? (
    <LazyVideo className="casestudies-media" src={`${project.video}#t=0.001`} />
  ) : (
    <img className="casestudies-media" src={project.photo} alt="" loading="lazy" />
  );
}

function ProjectCard({ project }) {
  const transitionTo = useTransitionNavigate();
  const open = () => transitionTo(project.path);
  const props = {
    role: "link",
    tabIndex: 0,
    onClick: open,
    onKeyDown: (e) => e.key === "Enter" && open(),
    "data-cursor": "custom",
    "data-cursor-icon": "arrow-up-right",
  };

  if (!project.featured) {
    return (
      <div {...props} className="casestudies-tile" aria-label={project.title} data-cursor-text={project.title.toUpperCase()}>
        <Media project={project} />
      </div>
    );
  }

  return (
    <div {...props} className="casestudies-feature" data-cursor-text="VIEW CASE STUDY">
      <div className="casestudies-feature-media">
        <Media project={project} />
      </div>
      <div className="stack stack--tight">
        <h3 className="h3">{project.title}</h3>
        <p className="text-small">{project.type}</p>
      </div>
    </div>
  );
}

export default function CaseStudies() {
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const [remoteMedia, setRemoteMedia] = useState([]);

  useEffect(() => {
    fetchWorkMedia().then(setRemoteMedia);
  }, []);

  // Parallax title, same as the other home sections
  useGSAP(() => {
    gsap.to(titleRef.current, {
      scrollTrigger: { trigger: containerRef.current, start: "top 50%", end: "top -20%", scrub: true },
      y: 150,
      filter: "blur(20px)",
      opacity: 0,
      ease: "none",
    });
  }, { scope: containerRef });

  return (
    <div className="section casestudies" ref={containerRef}>
      <h2 className="section-title" ref={titleRef}>Work</h2>
      {toGroups([...media, ...remoteMedia]).map((g) => (
        <Group key={g.category} {...g} />
      ))}
    </div>
  );
}
